import { useMemo } from 'react'
import { useDroppable, type DragEndEvent } from '@dnd-kit/core'
import {
	ReactFlow,
	ReactFlowProvider,
	Background,
	Controls,
	Handle,
	Position,
	type Edge,
	type Node,
	type NodeMouseHandler,
	type NodeTypes,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import type { WorkflowStepConfig, WorkflowStepType } from '@kfolks/types'
import { useWorkflowBuilderState } from './WorkflowBuilderState'
import { WorkflowStepNode } from './WorkflowStepNodes'

/**
 * Workflow canvas — the central authoring surface for the visual workflow
 * builder. Renders the in-progress `workflowConfig.steps[]` as `@xyflow/react`
 * nodes, one custom node renderer per step type (per ADR-0012's three-step
 * union: approval / execution / info_gate).
 *
 * Two distinct drag flows are wired through the parent `DndContext`:
 *
 *   1. **Palette drop** — an item from `NodePalette` (data:
 *      `{ source: 'palette', stepType }`) lands on the canvas drop zone.
 *      The canvas appends a new step of that type to the in-progress
 *      config and selects it so the properties panel (8.4) opens on it.
 *
 *   2. **Canvas-internal interactions** — clicking a step selects it.
 *      `@xyflow/react` natively supports drag-to-reposition; we don't
 *      persist node positions (the step-array order is what matters,
 *      and the canvas always lays out vertically).
 *
 * Read-only edges are rendered from each step's `after.next` field when
 * set: source = step's id, target = either another step's id or a
 * terminal-marker pill (rendered as a visual-only sink node). Edge
 * authoring (drawing edges to set `after.next`) lives in the properties
 * panel (task 8.4) — it's a single-target dropdown, not a free-form
 * canvas action, because the shipped step-array contract supports at
 * most ONE outgoing transition per step.
 *
 * Layout: vertical stack with a fixed gap. Dagre layout is a follow-up
 * (paired with task 8.6's validation badge); for the first cut a stable
 * predictable order matches the underlying `steps[]` array order.
 *
 * Compile-time exhaustiveness over `WorkflowStepType` — adding a new
 * step type to the union without a custom node renderer fails the
 * build via the `nodeTypes` map below.
 *
 * **Wraps in `ReactFlowProvider`** so the canvas works outside of a
 * single React Flow tree (multiple instances, hooks like
 * `useReactFlow()` accessible elsewhere). Cheap insurance.
 */
export function WorkflowCanvas() {
	return (
		<ReactFlowProvider>
			<WorkflowCanvasInner />
		</ReactFlowProvider>
	)
}

function WorkflowCanvasInner() {
	const { workflowConfig, selectStep } = useWorkflowBuilderState()

	const { setNodeRef: setDropRef, isOver } = useDroppable({
		id: 'workflow-canvas-drop-zone',
		data: { kind: 'canvas' },
	})

	// Node positions are derived from the canonical `steps[]` array order
	// (see `buildLayoutNodes`) — there's no free-form repositioning, so we
	// can pass nodes/edges directly to `<ReactFlow>` without going through
	// `useNodesState` / `useEdgesState`. Saves a sync `useEffect` and makes
	// the test surface (snapshot what we feed RF) trivial.
	const { nodes, edges } = useMemo(() => buildLayout(workflowConfig.steps), [workflowConfig.steps])

	const handleNodeClick: NodeMouseHandler = (_event, node) => {
		selectStep(node.id)
	}

	const isEmpty = workflowConfig.steps.length === 0

	return (
		<div
			ref={setDropRef}
			role="region"
			aria-label="Workflow canvas"
			data-canvas-drop-target="true"
			className={
				'relative h-[480px] rounded-md border bg-card transition-colors ' +
				(isOver ? 'border-primary bg-primary/5' : 'border-border')
			}
		>
			{isEmpty ? (
				<EmptyCanvasState />
			) : (
				<ReactFlow
					nodes={nodes}
					edges={edges}
					onNodeClick={handleNodeClick}
					nodeTypes={NODE_TYPES}
					nodesDraggable={false}
					fitView
					proOptions={{ hideAttribution: true }}
				>
					<Background />
					<Controls />
				</ReactFlow>
			)}
		</div>
	)
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function EmptyCanvasState() {
	return (
		<div className="flex h-full flex-col items-center justify-center p-8 text-center text-sm text-muted-foreground">
			<p className="font-medium">Drag a step from the palette to start</p>
			<p className="mt-1">Workflows always end at Completed, Rejected, or Cancelled.</p>
		</div>
	)
}

// ─── Layout helpers ──────────────────────────────────────────────────────────

const NODE_HEIGHT = 110
const NODE_GAP = 50
const TERMINAL_GAP = 70

const TERMINAL_KIND = ['completed', 'rejected', 'cancelled'] as const
type TerminalKind = (typeof TERMINAL_KIND)[number]

function isTerminalKind(value: string): value is TerminalKind {
	return (TERMINAL_KIND as readonly string[]).includes(value)
}

/**
 * Build the canvas layout in one pass: derive nodes (step nodes + any
 * terminal pill nodes referenced by edges) AND edges (one per step,
 * dashed for the implicit array-order default and solid for an
 * explicit `after.next` override).
 *
 * Implicit-vs-explicit semantics matches the engine: when a step has no
 * `after`, the engine advances to `steps[i+1]` (or terminates as
 * `'completed'` when it's the last step). The canvas mirrors that —
 * dashed edges show what the engine WILL do; solid edges show what the
 * admin EXPLICITLY authored. Distinct visuals so the admin can see at
 * a glance which transitions they own vs. which are defaults.
 *
 * Terminal markers (`'completed' | 'rejected' | 'cancelled'`) are not
 * steps — they're string literals in `after.next`. We render them as
 * small visual-only sink pills, deduplicated (one pill per terminal
 * marker referenced anywhere).
 */
function buildLayout(steps: ReadonlyArray<WorkflowStepConfig>): { nodes: Node[]; edges: Edge[] } {
	const nodes: Node[] = []
	const edges: Edge[] = []
	const terminalsReferenced = new Set<TerminalKind>()
	const stepIds = new Set(steps.map((s) => s.id))

	// Step nodes
	steps.forEach((step, idx) => {
		nodes.push({
			id: step.id,
			type: 'workflow-step',
			position: { x: 0, y: idx * (NODE_HEIGHT + NODE_GAP) },
			data: { step, isStart: idx === 0 },
		})
	})

	// Edges: one per step. Explicit `after.next` wins; otherwise fall
	// through to the next step in array order, or `'completed'` for the
	// last step.
	steps.forEach((step, idx) => {
		const explicit = step.after
		if (explicit) {
			const target = explicit.next
			const targetExists = stepIds.has(target) || isTerminalKind(target)
			if (!targetExists) return // validator surfaces this; skip rendering
			if (isTerminalKind(target)) terminalsReferenced.add(target)
			edges.push({
				id: `${step.id}__${target}`,
				source: step.id,
				target,
				label: explicit.on,
				type: 'smoothstep',
				animated: false,
				style: explicit.on === 'failure' ? { stroke: '#dc2626' } : undefined,
				data: { kind: 'explicit', on: explicit.on },
			})
			return
		}
		// Implicit default flow.
		const nextStep = steps[idx + 1]
		if (nextStep) {
			edges.push({
				id: `${step.id}__implicit__${nextStep.id}`,
				source: step.id,
				target: nextStep.id,
				type: 'smoothstep',
				style: { strokeDasharray: '5 5', stroke: '#9ca3af' },
				data: { kind: 'implicit' },
			})
		} else {
			// Last step → implicit completion.
			terminalsReferenced.add('completed')
			edges.push({
				id: `${step.id}__implicit__completed`,
				source: step.id,
				target: 'completed',
				type: 'smoothstep',
				style: { strokeDasharray: '5 5', stroke: '#9ca3af' },
				data: { kind: 'implicit' },
			})
		}
	})

	// Terminal pill nodes (deduplicated, one per kind referenced).
	const terminalsSorted = [...TERMINAL_KIND].filter((k) => terminalsReferenced.has(k))
	const lastStepY = (steps.length - 1) * (NODE_HEIGHT + NODE_GAP)
	const terminalRowY = lastStepY + NODE_HEIGHT + TERMINAL_GAP
	terminalsSorted.forEach((kind, i) => {
		nodes.push({
			id: kind,
			type: 'terminal-pill',
			position: { x: i * 180 - ((terminalsSorted.length - 1) * 180) / 2, y: terminalRowY },
			data: { kind },
		})
	})

	return { nodes, edges }
}

// ─── Node-types map ──────────────────────────────────────────────────────────

/**
 * `@xyflow/react` registers one node type — `'workflow-step'` — which
 * dispatches on `step.type` internally. This sidesteps the variance
 * issue between xyflow's shared `ComponentType<NodeProps>` and our
 * narrower per-`WorkflowStepConfig` renderers (which previously needed
 * a double-cast at the registration seam). End-to-end type safety: all
 * narrowing happens inside `WorkflowStepNode`'s `switch` over
 * `step.type` with a `: never` default that's the compile-time
 * exhaustiveness check (adding a new step-type discriminator without a
 * `case` is a build error).
 */
const NODE_TYPES: NodeTypes = {
	'workflow-step': WorkflowStepNode,
	'terminal-pill': TerminalPillNode,
}

/**
 * Visual-only sink node for terminal markers (`completed` / `rejected` /
 * `cancelled`). One pill per marker referenced anywhere on the canvas
 * (deduplicated by `buildLayout`). Has a target handle only — the admin
 * can author edges INTO a pill via the properties panel's "Next"
 * dropdown but never drags FROM one. Click is a no-op (no selection).
 */
function TerminalPillNode({ data }: { data: { kind: TerminalKind } }) {
	const styles: Record<TerminalKind, { bg: string; text: string; border: string; label: string }> = {
		completed: {
			bg: 'bg-emerald-50',
			text: 'text-emerald-700',
			border: 'border-emerald-300',
			label: 'Completed',
		},
		rejected: {
			bg: 'bg-rose-50',
			text: 'text-rose-700',
			border: 'border-rose-300',
			label: 'Rejected',
		},
		cancelled: {
			bg: 'bg-gray-100',
			text: 'text-gray-600',
			border: 'border-gray-300',
			label: 'Cancelled',
		},
	}
	const s = styles[data.kind]
	return (
		<div
			className={`rounded-full border px-4 py-1 text-xs font-semibold uppercase tracking-wide shadow-sm ${s.bg} ${s.text} ${s.border}`}
			data-terminal-kind={data.kind}
		>
			<Handle type="target" position={Position.Top} isConnectable={false} />
			{s.label}
		</div>
	)
}

// ─── Drag-end dispatcher (mounted by WorkflowBuilderPage on its DndContext) ──

/**
 * Build the `onDragEnd` handler for the parent `DndContext`. Lives here
 * (next to the canvas) so the dispatch logic stays close to the consumer.
 *
 * Today: palette → canvas drop only. Canvas-internal reorder is a
 * follow-up — `@xyflow/react`'s built-in node-drag persistence is one
 * path, but the array-order canonical layout we use here makes it a
 * reorder (move a step up/down) more than a free-form reposition. That
 * UX lives next to the properties panel in task 8.4.
 */
export interface WorkflowDragEndDispatch {
	insertStep: (stepType: WorkflowStepType, atIndex?: number) => string
	selectStep: (id: string | null) => void
}

export function dispatchWorkflowDragEnd(event: DragEndEvent, deps: WorkflowDragEndDispatch): void {
	const { active, over } = event
	if (!over) return
	const activeData = active.data.current as { source?: string; stepType?: WorkflowStepType } | undefined
	if (activeData?.source === 'palette' && activeData.stepType) {
		const newId = deps.insertStep(activeData.stepType)
		deps.selectStep(newId)
	}
}
